<template>
  <div
    v-if="show && feature"
    class="ags-popup-overlay"
    @click.self="$emit('close')"
  >
    <div class="ags-popup-content">
      <!-- Header -->
      <div class="ags-popup-header">
        <div>
          <h3 class="ags-popup-title">{{ feature.properties.name || 'Geographic Unit' }}</h3>
          <p class="ags-popup-subtitle">{{ layerType.replace('_', ' ').toUpperCase() }}: {{ feature.properties.state_code || feature.properties.code || 'N/A' }}</p>
        </div>
        <button @click="$emit('close')" class="ags-popup-close" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <!-- Body -->
      <div class="ags-popup-body">
        <!-- Geographic Information -->
        <div class="ags-popup-section">
          <h4 class="ags-popup-section-title">Geographic Information</h4>
          <div class="ags-popup-data-row">
            <div class="ags-popup-label">State:</div>
            <div class="ags-popup-value">{{ feature.properties.name || 'N/A' }} ({{ feature.properties.state_code || 'N/A' }})</div>
          </div>
          <div v-if="feature.properties.population" class="ags-popup-data-row">
            <div class="ags-popup-label">Population:</div>
            <div class="ags-popup-value">{{ formatNumber(feature.properties.population) }}</div>
          </div>
        </div>
        
        <!-- AGS Data Analysis -->
        <div v-if="selectedVariables.length > 0 && feature.properties.ags_intensity_level" class="ags-popup-section">
          <h4 class="ags-popup-section-title">AGS Data Analysis</h4>
          
          <!-- Data Intensity -->
          <div class="ags-popup-data-row">
            <div class="ags-popup-label">Data Intensity:</div>
            <div class="ags-popup-value">
              <span class="ags-intensity-badge" :class="'intensity-' + feature.properties.ags_intensity_level">
                {{ formatIntensityLevel(feature.properties.ags_intensity_level) }}
              </span>
              <span class="ags-intensity-percent">{{ getIntensityPercent() }}%</span>
            </div>
          </div>
          
          <!-- Classification -->
          <div class="ags-popup-data-row">
            <div class="ags-popup-label">Classification:</div>
            <div class="ags-popup-value">
              <span class="ags-classification-badge" :class="'classification-' + feature.properties.ags_intensity_level">
                {{ formatIntensityLevel(feature.properties.ags_intensity_level).toUpperCase() }}
              </span>
            </div>
          </div>
          
          <!-- Ranking -->
          <div v-if="feature.properties.ags_comparison_reference" class="ags-popup-data-row">
            <div class="ags-popup-label">Ranking:</div>
            <div class="ags-popup-value">
              {{ getRankingText() }}
              <span class="ags-ranking-badge" :class="getRankingBadgeClass()">
                {{ getRankingPercentile() }}
              </span>
            </div>
          </div>
        </div>
        
        <!-- Variable Values Table -->
        <div v-if="selectedVariables.length > 0" class="ags-popup-section">
          <h4 class="ags-popup-section-title">
            {{ getAgsDataCategoryName() }} ({{ new Date().getFullYear() }})
          </h4>
          
          <div class="ags-popup-table">
            <div class="ags-popup-table-header">
              <div class="ags-popup-table-cell">VARIABLE</div>
              <div class="ags-popup-table-cell text-right">VALUE</div>
            </div>
            
            <!-- Single variable -->
            <div v-if="selectedVariables.length === 1" class="ags-popup-table-row">
              <div class="ags-popup-table-cell">{{ selectedVariables[0].label }}</div>
              <div class="ags-popup-table-cell text-right font-semibold">
                {{ feature.properties.ags_value !== null && feature.properties.ags_value !== undefined
                   ? formatAgsVariableValue(feature.properties.ags_value, selectedVariables[0].format)
                   : 'N/A' }}
              </div>
            </div>
            
            <!-- Multiple variables -->
            <div v-else v-for="variable in selectedVariables" :key="variable.path" class="ags-popup-table-row">
              <div class="ags-popup-table-cell">{{ variable.label }}</div>
              <div class="ags-popup-table-cell text-right font-semibold">
                {{ getAgsVariableValue(feature.properties.ags_data, variable) }}
              </div>
            </div>
          </div>
        </div>
        
        <!-- Debug Information (Collapsible) -->
        <div v-if="selectedVariables.length > 0 && feature.properties.ags_comparison_reference" class="ags-popup-section">
          <button @click="showDebugInfo = !showDebugInfo" class="ags-debug-toggle">
            🔍 AGS DATA DEBUG INFORMATION
            <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': showDebugInfo }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          
          <div v-if="showDebugInfo" class="ags-debug-content">
            <!-- Step 1: Selected Variables -->
            <div class="ags-debug-step">
              <h5 class="ags-debug-step-title">STEP 1: SELECTED VARIABLES & EXTRACTED VALUES</h5>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Total Selected Variables:</span>
                <span class="ags-debug-value">{{ selectedVariables.length }}</span>
              </div>
              <div v-for="variable in selectedVariables" :key="variable.path" class="ags-debug-item">
                <span class="ags-debug-label">{{ variable.label }}:</span>
                <span class="ags-debug-value">{{ getAgsVariableRawValue(feature.properties.ags_data, variable) }}</span>
              </div>
            </div>
            
            <!-- Step 2: AGS Data Range -->
            <div v-if="feature.properties.ags_comparison_reference" class="ags-debug-step">
              <h5 class="ags-debug-step-title">STEP 2: AGS DATA RANGE (BACKEND CALCULATED)</h5>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Min AGS Index ({{ comparisonMode }}):</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.ags_comparison_reference.min_value) }}</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Max AGS Index ({{ comparisonMode }}):</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.ags_comparison_reference.max_value) }}</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Total Entities:</span>
                <span class="ags-debug-value">{{ feature.properties.ags_comparison_reference.total_entities }}</span>
              </div>
            </div>
            
            <!-- Step 3: Current Unit Value -->
            <div class="ags-debug-step">
              <h5 class="ags-debug-step-title">STEP 3: CURRENT UNIT AGS VALUE</h5>
              <div class="ags-debug-item">
                <span class="ags-debug-label">AGS Value:</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.ags_value) }}</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Intensity Level:</span>
                <span class="ags-debug-value">{{ feature.properties.ags_intensity_level }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgsDataPopup',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    feature: {
      type: Object,
      default: null
    },
    selectedVariables: {
      type: Array,
      default: () => []
    },
    comparisonMode: {
      type: String,
      default: 'viewport'
    },
    layerType: {
      type: String,
      default: 'states'
    }
  },
  
  data() {
    return {
      showDebugInfo: false
    };
  },
  
  methods: {
    getAgsVariableValue(agsData, variable) {
      const rawValue = this.getAgsVariableRawValue(agsData, variable);
      if (rawValue === null) return 'N/A';
      return this.formatAgsVariableValue(rawValue, variable.format);
    },
    
    getAgsVariableRawValue(agsData, variable) {
      if (!agsData) return null;
      
      for (const categoryKey in agsData) {
        const categoryData = agsData[categoryKey];
        if (categoryData && categoryData.extracted_values) {
          const value = categoryData.extracted_values[variable.path];
          if (value !== undefined && value !== null) {
            return value;
          }
        }
      }
      
      return null;
    },
    
    getAgsDataCategoryName() {
      if (!this.feature || !this.feature.properties.ags_data) return 'AGS Data';
      
      const agsData = this.feature.properties.ags_data;
      for (const categoryKey in agsData) {
        const categoryData = agsData[categoryKey];
        if (categoryData && categoryData.category_display_name) {
          return categoryData.category_display_name;
        }
      }
      
      return 'AGS Data';
    },
    
    formatIntensityLevel(level) {
      if (!level) return 'N/A';
      
      const levels = {
        'very_low': 'Very Low',
        'low': 'Low',
        'low_moderate': 'Low-Moderate',
        'moderate': 'Moderate',
        'moderate_high': 'Moderate-High',
        'high': 'High',
        'very_high': 'Very High',
        'no_data': 'No Data'
      };
      
      return levels[level] || level;
    },
    
    getIntensityPercent() {
      const levels = ['very_low', 'low', 'low_moderate', 'moderate', 'moderate_high', 'high', 'very_high'];
      const currentLevel = this.feature.properties.ags_intensity_level;
      const index = levels.indexOf(currentLevel);
      
      if (index === -1) return 0;
      
      return Math.round(((index + 1) / levels.length) * 100);
    },
    
    getRankingText() {
      const ref = this.feature.properties.ags_comparison_reference;
      if (!ref || !ref.total_entities) return 'N/A';
      
      const value = this.feature.properties.ags_value;
      const min = ref.min_value;
      const max = ref.max_value;
      
      // Calculate approximate rank based on value position in range
      const range = max - min;
      const position = value - min;
      const percentile = range > 0 ? (position / range) * 100 : 50;
      const rank = Math.floor((percentile / 100) * ref.total_entities) + 1;
      
      return `${rank} out of ${ref.total_entities} in ${ref.type}`;
    },
    
    getRankingPercentile() {
      const ref = this.feature.properties.ags_comparison_reference;
      if (!ref) return '';
      
      const value = this.feature.properties.ags_value;
      const min = ref.min_value;
      const max = ref.max_value;
      
      const range = max - min;
      const position = value - min;
      const percentile = range > 0 ? Math.round((position / range) * 100) : 50;
      
      if (percentile <= 25) return 'BOTTOM 25%';
      if (percentile <= 50) return 'BOTTOM 50%';
      if (percentile >= 75) return 'TOP 25%';
      return 'MIDDLE 50%';
    },
    
    getRankingBadgeClass() {
      const percentile = this.getRankingPercentile();
      if (percentile.includes('BOTTOM 25')) return 'ranking-bottom';
      if (percentile.includes('TOP 25')) return 'ranking-top';
      return 'ranking-middle';
    },
    
    formatAgsVariableValue(value, format) {
      if (value === null || value === undefined) return 'N/A';
      
      const num = parseFloat(value);
      if (isNaN(num)) return 'N/A';
      
      if (format === 'currency') {
        if (num >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'k';
        return '$' + num.toLocaleString();
      } else if (format === 'percent') {
        return (num * 100).toFixed(1) + '%';
      } else {
        return this.formatNumber(num);
      }
    },
    
    formatNumber(value) {
      if (value === null || value === undefined) return 'N/A';
      
      const num = parseFloat(value);
      if (isNaN(num)) return 'N/A';
      
      if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B';
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
      
      return num.toLocaleString();
    }
  }
};
</script>

<style scoped>
/* AGS Popup Overlay */
.ags-popup-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.ags-popup-content {
  background-color: var(--background);
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 600px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
}

/* Header */
.ags-popup-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
}

.ags-popup-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--foreground);
  margin: 0;
}

.ags-popup-subtitle {
  font-size: 0.875rem;
  color: var(--muted-foreground);
  margin-top: 0.25rem;
  font-weight: 500;
}

.ags-popup-close {
  background: none;
  border: none;
  color: var(--muted-foreground);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
  flex-shrink: 0;
}

.ags-popup-close:hover {
  background-color: var(--muted);
  color: var(--foreground);
}

/* Body */
.ags-popup-body {
  padding: 1.5rem;
}

.ags-popup-section {
  margin-bottom: 1.5rem;
}

.ags-popup-section:last-child {
  margin-bottom: 0;
}

.ags-popup-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--foreground);
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ags-popup-data-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
}

.ags-popup-data-row:last-child {
  border-bottom: none;
}

.ags-popup-label {
  font-size: 0.875rem;
  color: var(--muted-foreground);
  font-weight: 500;
}

.ags-popup-value {
  font-size: 0.875rem;
  color: var(--foreground);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Table */
.ags-popup-table {
  border: 1px solid var(--border);
  border-radius: 0.375rem;
  overflow: hidden;
}

.ags-popup-table-header {
  display: flex;
  background-color: var(--muted);
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted-foreground);
}

.ags-popup-table-row {
  display: flex;
  border-top: 1px solid var(--border);
  background-color: var(--background);
}

.ags-popup-table-row:hover {
  background-color: var(--muted);
}

.ags-popup-table-cell {
  flex: 1;
  padding: 0.75rem;
  font-size: 0.875rem;
  color: var(--foreground);
}

.ags-popup-table-cell.text-right {
  text-align: right;
}

.ags-popup-table-cell.font-semibold {
  font-weight: 600;
}

/* Intensity Badge */
.ags-intensity-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.ags-intensity-badge.intensity-very_low { background-color: #d1fae5; color: #065f46; }
.ags-intensity-badge.intensity-low { background-color: #dcfce7; color: #166534; }
.ags-intensity-badge.intensity-low_moderate { background-color: #fef9c3; color: #854d0e; }
.ags-intensity-badge.intensity-moderate { background-color: #fef3c7; color: #92400e; }
.ags-intensity-badge.intensity-moderate_high { background-color: #ffedd5; color: #9a3412; }
.ags-intensity-badge.intensity-high { background-color: #fee2e2; color: #991b1b; }
.ags-intensity-badge.intensity-very_high { background-color: #fecaca; color: #7f1d1d; }

.ags-intensity-percent {
  font-size: 0.875rem;
  color: var(--muted-foreground);
}

/* Classification Badge */
.ags-classification-badge {
  padding: 0.375rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.ags-classification-badge.classification-very_low,
.ags-classification-badge.classification-low {
  background-color: #22c55e;
  color: white;
}

.ags-classification-badge.classification-low_moderate,
.ags-classification-badge.classification-moderate {
  background-color: #eab308;
  color: white;
}

.ags-classification-badge.classification-moderate_high,
.ags-classification-badge.classification-high,
.ags-classification-badge.classification-very_high {
  background-color: #ef4444;
  color: white;
}

/* Ranking Badge */
.ags-ranking-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 700;
}

.ags-ranking-badge.ranking-bottom {
  background-color: #fee2e2;
  color: #991b1b;
}

.ags-ranking-badge.ranking-middle {
  background-color: #fef3c7;
  color: #92400e;
}

.ags-ranking-badge.ranking-top {
  background-color: #d1fae5;
  color: #065f46;
}

/* Debug Section */
.ags-debug-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background-color: #fef3c7;
  border: 2px solid #f59e0b;
  border-radius: 0.5rem;
  color: #92400e;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.ags-debug-toggle:hover {
  background-color: #fde68a;
}

.ags-debug-content {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #fffbeb;
  border: 2px solid #fbbf24;
  border-radius: 0.5rem;
}

.ags-debug-step {
  margin-bottom: 1.5rem;
}

.ags-debug-step:last-child {
  margin-bottom: 0;
}

.ags-debug-step-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #fbbf24;
}

.ags-debug-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: 0.875rem;
}

.ags-debug-label {
  color: #78350f;
  font-weight: 500;
}

.ags-debug-value {
  color: #92400e;
  font-weight: 600;
  font-family: monospace;
}

.rotate-180 {
  transform: rotate(180deg);
}
</style>
